"use client";
import dynamic from "next/dynamic";
import NextImage from "next/image";
import React, { useCallback, useEffect, useRef, useState } from "react";

import Lightbox from "@/components/surfaces/lightbox";
import { getSharePageUrl } from "@/utils/share-page-url";

function formatDoiUrl(doi: string): string {
  const doiPrefix = "https://doi.org/";
  if (doi.startsWith("http://") || doi.startsWith("https://")) {
    // If the DOI is already a full URL, return it as is
    return doi;
  } else if (doi.startsWith("doi.org/")) {
    // If the DOI starts with 'doi.org/', prepend 'https://'
    return `https://${doi}`;
  } else if (doi.startsWith("10.")) {
    // If the DOI starts with '10.' (standard DOI prefix), prepend the full URL
    return `${doiPrefix}${doi}`;
  } else {
    // For any other case, assume it's a bare DOI and prepend the full URL
    return `${doiPrefix}${doi}`;
  }
}

const SharePage = dynamic(() => import("@/components/social/share-page"), { ssr: false });

interface Author {
  readonly name: string;
}

interface ScholarlyArticleProps {
  readonly headline: string;
  readonly authors: readonly Author[];
  readonly publishedDate: string;
  readonly journalName: string;
  readonly doiUrl: string;
  readonly articleUrl: string;
  readonly logoUrl?: string;
  readonly logoAlt?: string;
  readonly logoHeight?: number;
  readonly logoWidth?: number;
  readonly featuredImage?: {
    readonly url: string;
    readonly width: number;
    readonly height: number;
    readonly alternativeText: string;
  };
  readonly images?: readonly {
    readonly url: string;
    readonly width: number;
    readonly height: number;
    readonly alternativeText: string;
  }[];
}

// eslint-disable-next-line max-lines-per-function -- complex article card rendering
export default function ScholarlyArticle({
  headline,
  authors,
  publishedDate,
  journalName,
  doiUrl,
  articleUrl,
  logoUrl,
  logoAlt,
  logoHeight = 92,
  logoWidth = 512,
  // featuredImage,
  images = [],
}: ScholarlyArticleProps): React.JSX.Element {
  const imagesLoadedRef = useRef(false);

  const cacheImages = useCallback((images: readonly { url: string }[]): (() => void) => {
    const controller = new AbortController();
    const { signal } = controller;

    images.forEach((image) => {
      if (signal.aborted) return;

      const img = new Image();
      // eslint-disable-next-line functional/immutable-data -- DOM Image API requires direct property assignment
      img.src = image.url;
      // eslint-disable-next-line functional/immutable-data -- DOM Image API requires direct property assignment
      img.onload = img.onerror = function (): void {
        if (signal.aborted) return;
        // You could add logic here to track loading progress if needed
      };
    });

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line functional/no-let -- cleanup ref must be reassigned conditionally
    let cleanup: (() => void) | undefined;

    if (images.length > 0 && !imagesLoadedRef.current) {
      cleanup = cacheImages(images);

      imagesLoadedRef.current = true;
    }

    return function cleanupFunction(): void {
      if (cleanup) {
        cleanup();
      }
    };
  }, [images, cacheImages]);

  const [lightboxImage, setLightboxImage] = useState<{
    url: string;
    alt: string;
    width: number;
    height: number;
  } | null>(null);

  const openLightbox = useCallback(
    (imageUrl: string, altText: string, width: number, height: number) => {
      setLightboxImage({ alt: altText, height, url: imageUrl, width });
    },
    [],
  );

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
  }, []);

  return (
    <>
      <article
        className="scholarly-article"
        itemType="http://schema.org/ScholarlyArticle"
        itemScope
      >
        <header>
          <h2 itemProp="headline">
            <a href={articleUrl} rel="noreferrer" target="_blank">
              {headline}
            </a>
          </h2>
        </header>
        <ul className="authors">
          {authors.map((author, index) => (
            <li itemProp="author" itemType="http://schema.org/Person" key={index} itemScope>
              <span itemProp="name">{author.name}</span>
            </li>
          ))}
        </ul>
        <div className="article-date-publisher-and-doi-container">
          <div className="article-date-publisher-and-doi-container-cell">
            <dl
              className="article-date-and-publisher"
              itemProp="isPartOf"
              itemType="http://schema.org/Periodical"
              itemScope
            >
              <dt>Published:</dt>
              <dd className="date-published">
                <time dateTime={publishedDate} itemProp="datePublished">
                  {new Date(publishedDate).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </time>
              </dd>
              <dt>Publisher:</dt>
              <dd itemProp="name">{journalName}</dd>
            </dl>
            <dl className="doi">
              <dt>DOI:</dt>
              <dd>
                <a
                  aria-label="View this article at doi.org"
                  className="inline-text-link"
                  href={formatDoiUrl(doiUrl)}
                  itemProp="url"
                  rel="noreferrer"
                  target="_blank"
                >
                  {doiUrl}
                </a>
              </dd>
            </dl>
          </div>
          {logoUrl && (
            <div className="article-date-publisher-and-doi-container-cell article-date-publisher-and-doi-container-cell-publisher-logo">
              <a
                aria-label="View this article on the publisher's website"
                className="publisher-logo-container"
                href={articleUrl}
                rel="noreferrer"
                target="_blank"
              >
                <NextImage
                  alt={logoAlt ?? "Journal Logo"}
                  height={logoHeight}
                  itemProp="logo"
                  src={logoUrl}
                  width={logoWidth}
                />
              </a>
            </div>
          )}
        </div>
        <SharePage text={headline} url={getSharePageUrl()} />
        {images.length > 0 && (
          <ul
            className="scholarly-article-image-gallery"
            itemType="https://schema.org/ImageGallery"
            itemScope
          >
            {images.map((image, index) => (
              <li itemProp="image" itemType="https://schema.org/ImageObject" key={index}>
                <button
                  aria-label={`Open image ${index + 1}`}
                  className="lightbox-trigger"
                  onClick={() =>
                    openLightbox(
                      image.url,
                      image.alternativeText || `Image ${index + 1}`,
                      image.width,
                      image.height,
                    )
                  }
                >
                  <NextImage
                    alt={image.alternativeText || `Image ${index + 1}`}
                    height={image.height}
                    loading="lazy" // Add lazy loading to gallery images
                    src={image.url}
                    width={image.width}
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </article>
      {lightboxImage && (
        <Lightbox
          altText={lightboxImage.alt}
          height={lightboxImage.height}
          imageUrl={lightboxImage.url}
          width={lightboxImage.width}
          onClose={closeLightbox}
        />
      )}
    </>
  );
}
